import { get } from 'ctm-util-functions';

import { LayoutFactory } from './layout_factory';
import { BaseBuilder } from '../base_builder';

/**
 * Given a schema object construct the page layout
 */
class LayoutBuilder extends BaseBuilder {

    /**
     * @desc Initialise the builder instance
     * @param {Object} schemaModel - the complete layoutSchemaModel that describes the layout that will be built
     * @constructor
     */
    constructor(schemaModel) {
        super(schemaModel);
        this.factory = new LayoutFactory();
    }

    /**
     * Build layout(flexbox) components defined in layout schema,
     * assign/spread components built by other builders to their own layout area defined in layoutComponentSchemaMapping.
     * @param {String} mappingKey - layoutComponentMappingKey to identify which layout and components to build
     * @param {Object} extraProps - extraProps you want to pass down to all sub components
     * @returns {Array} list of Flexbox components with with appropriate children and props
     */
    build = (mappingKey, extraProps) => {
        /**
         * @example
         * layoutComponentMappingKey: {
                'layoutSchemaName': 'layoutJourneyDefault', // or layoutMappingName: 'container_full_width_header'
                'layoutComponentMaps': {
                    'header': ['layout_energyJourneyHeader'],
                    'content': ['form_start', 'component_nextStepButtonStart'],
                    'sidebar': ['component_boxFreeService'],
                    'footer': ['component_footerDefault']
                }
            }
         */

        const layoutComponentMapping = this.schemaModel.getLayoutComponentMapping(mappingKey);
        if (!layoutComponentMapping) {
            throw new Error(`Layout Component Mapping does not exist for ${mappingKey}.`);
        }
        const layoutComponentMaps = layoutComponentMapping.layoutComponentMaps;
        const layoutSchemaKey = layoutComponentMapping.layoutMappingName || layoutComponentMapping.layoutSchemaName;
        const layoutSchemaType = layoutComponentMapping.layoutMappingName ? 'mapping' : 'element';

        if (!layoutComponentMaps || !layoutSchemaKey) {
            throw new Error(`The layout mapping schema for ${mappingKey} does not have proper mapping defined.`);
        }

        const layoutMapping = this.schemaModel.getLayoutMappingWithBreakpoint(layoutSchemaKey, layoutSchemaType, get(this.props, 'pageState.breakpoint', '')) || [];

        return this.buildComponent(layoutMapping, extraProps, layoutComponentMaps);
    };

    /**
     * Build individual Flexbox component and its children based on the schema
     * @param {Object} layoutMapping - list of layout elements name to be built
     * @param {Object} extraProps - extraProps you want to pass down to all sub components
     * @param {Object} layoutComponentMaps - A mapping of layout area to component keys
     * @returns {Array} a list of Flexbox components with appropriate children and props
     */
    buildComponent = (layoutMapping, extraProps, layoutComponentMaps) => {
        const layoutComponents = [];
        // Build layout components and their children for each schema
        for (const layoutSchemaName of layoutMapping) {
            const layoutSchema = this.schemaModel.getMergedSchema(layoutSchemaName);
            if (!layoutSchema) {
                throw Error(`The layout schema name you have provided does not exist: ${layoutSchemaName}`);
            }
            const children = this.buildChildren(layoutSchema, extraProps, layoutComponentMaps);
            const component = this.factory.createLayoutComponent(layoutSchemaName, layoutSchema, children);
            layoutComponents.push(component);
        }
        return layoutComponents;
    };

    /**
     * build the child components to pass to its parent Flexbox
     * @param {Object} layoutSchema - parent schema that describes how the Flexbox and its children should be built
     * @param {Object} extraProps - extraProps you want to pass down to all sub components
     * @param {Object} layoutComponentMaps - A mapping of layout area to component keys
     * @returns {Array} children - an array of children to be injected inside Flexbox
     */
    buildChildren = (layoutSchema, extraProps, layoutComponentMaps) => {
        const childrenLayoutMapping = layoutSchema.children || [];
        const children = this.buildComponent(childrenLayoutMapping, extraProps, layoutComponentMaps);

        // concat with components built by other builders at the parent level, using builder director
        const mappedComponentNameList = layoutComponentMaps[layoutSchema.layoutAreaKey];
        const mappedComponents = mappedComponentNameList && this.props.director.constructComponents(mappedComponentNameList, extraProps);
        return children.concat(mappedComponents || []);
    };

}

export { LayoutBuilder };
